import DOMPurify from "dompurify";
import htmlToPdfmake from "html-to-pdfmake";
import PdfPrinter from "pdfmake";

import { EMAIL_ID, HOSPITAL_ADDRESS, PHONE_NO } from "./index";

const fonts = require("pdfmake/build/vfs_fonts");
const PATIENT_CASE_STUDY_HOSPITAL_NAME =
  "Sri Vedamaayu Ayurveda Hospital and Wellness Centre";
let smallLogoPromise;

// pdfMake 0.1.x requires a data URL, so load the approved public logo once.
const getSmallLogoDataUrl = () => {
  if (!smallLogoPromise) {
    const publicPath = process.env.PUBLIC_URL || "";

    smallLogoPromise = fetch(`${publicPath}/vedamaayu_small_logo.png`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load Patient Case Study logo.");
        }

        return response.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () =>
              reject(new Error("Unable to convert Patient Case Study logo."));
            reader.readAsDataURL(blob);
          }),
      )
      .catch((error) => {
        smallLogoPromise = null;
        throw error;
      });
  }

  return smallLogoPromise;
};

const firstValue = (...values) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== "",
  );

const getPatientDetails = (patientInfo, patientData) => {
  const primary = patientInfo || {};
  const fallback = patientData || {};
  const fullName = firstValue(
    primary.fullName,
    primary.name,
    [
      primary.fname || primary.first_name || primary.firstName,
      primary.mname || primary.middle_name || primary.middleName,
      primary.lname || primary.last_name || primary.lastName,
    ]
      .filter(Boolean)
      .join(" "),
    fallback.fullName,
    [fallback.fname, fallback.mname, fallback.lname].filter(Boolean).join(" "),
  );

  return {
    main: firstValue(primary.id, fallback.id, "-"),
    name: fullName || "-",
    phone: firstValue(
      primary.phno,
      primary.phoneNumber,
      primary.mobile,
      fallback.phno,
      fallback.phoneNumber,
      "-",
    ),
    age: firstValue(primary.age, fallback.age, "-"),
    gender: firstValue(primary.gender, fallback.gender, "-"),
    address:
      firstValue(
        primary.address,
        fallback.address,
        [fallback.area, fallback.city, fallback.state]
          .filter(Boolean)
          .join(", "),
      ) || "-",
  };
};

const getInlineDeltaText = (text, attributes = {}) => ({
  text,
  ...(attributes.bold ? { bold: true } : {}),
  ...(attributes.italic ? { italics: true } : {}),
  ...(attributes.underline ? { decoration: "underline" } : {}),
});

const getDeltaLine = (inlineContent, attributes = {}) => {
  const line = {
    text: inlineContent.length ? inlineContent : " ",
    margin: [0, 0, 0, 3],
  };

  if (attributes.header) {
    line.bold = true;
    line.fontSize =
      attributes.header === 1 ? 14 : attributes.header === 2 ? 12 : 10;
  }

  if (attributes.align) {
    line.alignment = attributes.align;
  }

  if (attributes.blockquote) {
    line.italics = true;
    line.color = "#374151";
    line.margin = [10, 2, 0, 5];
  }

  return line;
};

const deltaToPdfContent = (delta) => {
  const blocks = [];
  let inlineContent = [];

  const appendLine = (attributes = {}) => {
    const line = getDeltaLine(inlineContent, attributes);
    inlineContent = [];

    if (attributes.list) {
      const listType = attributes.list === "ordered" ? "ol" : "ul";
      const previousBlock = blocks[blocks.length - 1];

      if (previousBlock?.[listType]) {
        previousBlock[listType].push(line);
      } else {
        blocks.push({
          [listType]: [line],
          margin: [12, 0, 0, 3],
        });
      }
      return;
    }

    blocks.push(line);
  };

  (delta?.ops || []).forEach((operation) => {
    if (typeof operation.insert !== "string") {
      return;
    }

    const parts = operation.insert.split("\n");

    parts.forEach((part, index) => {
      if (part) {
        inlineContent.push(getInlineDeltaText(part, operation.attributes));
      }

      if (index < parts.length - 1) {
        appendLine(operation.attributes);
      }
    });
  });

  if (inlineContent.length) {
    appendLine();
  }

  return blocks.length
    ? blocks
    : [{ text: "No notes available", color: "#6b7280" }];
};

const parseStoredContent = (content) => {
  if (Array.isArray(content?.ops)) {
    return content;
  }

  if (typeof content !== "string") {
    return content;
  }

  const trimmedContent = content.trim();

  if (!trimmedContent.startsWith("{")) {
    return content;
  }

  try {
    const parsedContent = JSON.parse(trimmedContent);

    if (Array.isArray(parsedContent?.ops)) {
      return parsedContent;
    }

    if (parsedContent?.notes !== undefined) {
      return parseStoredContent(parsedContent.notes);
    }
  } catch (error) {
    return content;
  }

  return content;
};

// Sanitize saved Quill HTML before converting it to PDF content.
const normalizeQuillHtml = (html) => {
  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(html);

  ["center", "right", "justify"].forEach((alignment) => {
    container.querySelectorAll(`.ql-align-${alignment}`).forEach((element) => {
      element.style.textAlign = alignment;
    });
  });

  return container.innerHTML;
};

const getRichTextContent = (content) => {
  const storedContent = parseStoredContent(content);

  if (Array.isArray(storedContent?.ops)) {
    return deltaToPdfContent(storedContent);
  }

  const html = typeof storedContent === "string" ? storedContent : "";
  const sanitized = normalizeQuillHtml(html).replace(
    /<p><br\s*\/?><\/p>/gi,
    "",
  );

  if (!sanitized.trim()) {
    return [{ text: "No notes available", color: "#6b7280" }];
  }

  const converted = htmlToPdfmake(sanitized, {
    defaultStyles: {
      b: { bold: true },
      strong: { bold: true },
      i: { italics: true },
      em: { italics: true },
      u: { decoration: "underline" },
      p: { margin: [0, 0, 0, 3] },
      ul: { margin: [0, 2, 0, 3] },
      ol: { margin: [0, 2, 0, 3] },
    },
  });

  return Array.isArray(converted) ? converted : [converted];
};

// Formats the Case Study date for the PDF without changing its stored value.
const formatCaseStudyDate = (value) => {
  if (!value) return "Not available";

  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      )
    : new Date(value);

  if (Number.isNaN(date.getTime())) return "Not available";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Formats the stored creation timestamp as a 12-hour time for the PDF.
const formatCaseStudyCreatedTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Keep the Doctor/Date header with the beginning of its Notes section.
const createCaseStudySection = (caseStudy, index) => ({
  margin: [0, index === 0 ? 0 : 10, 0, 0],
  table: {
    headerRows: 1,
    keepWithHeaderRows: 1,
    widths: ["*"],
    body: [
      [
        {
          margin: [10, 7, 10, 7],
          columns: [
            {
              text: [
                { text: "Doctor Name: ", bold: true },
                caseStudy.doctorName || "Not available",
              ],
              width: "*",
            },
            {
              stack: [
                {
                  text: formatCaseStudyDate(caseStudy.date),
                },
                ...(formatCaseStudyCreatedTime(caseStudy.createdAt)
                  ? [
                      {
                        text: formatCaseStudyCreatedTime(caseStudy.createdAt),
                        color: "#64748b",
                        fontSize: 7,
                        margin: [0, 2, 0, 0],
                      },
                    ]
                  : []),
              ],
              width: "auto",
              alignment: "right",
            },
          ],
          fontSize: 8,
        },
      ],
      [
        {
          margin: [10, 7, 10, 9],
          stack: [
            {
              text: "Notes",
              bold: true,
              fontSize: 8,
              margin: [0, 0, 0, 4],
            },
            {
              stack: getRichTextContent(caseStudy.notes),
              fontSize: 8,
              lineHeight: 1.2,
            },
          ],
        },
      ],
    ],
  },
  layout: {
    hLineColor: () => "#b7c7d8",
    vLineColor: () => "#b7c7d8",
    hLineWidth: () => 0.7,
    vLineWidth: () => 0.7,
  },
});

// Build the white A4 Patient Case Study print document.
export const generatePatientCaseStudyPdf = ({
  patientInfo,
  patientData,
  caseStudies,
}) => {
  const patient = getPatientDetails(patientInfo, patientData);

  return getSmallLogoDataUrl().then((smallLogo) => ({
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [40, 80, 40, 65],
    header: (currentPage) => ({
      margin: [40, 8, 40, 0],
      stack: [
        {
          columns: [
            {
              image: smallLogo,
              fit: [55, 48],
              width: 60,
            },
            {
              width: "*",
              alignment: "center",
              margin: [0, 5, 0, 0],
              stack: [
                {
                  text: PATIENT_CASE_STUDY_HOSPITAL_NAME,
                  color: "#176fad",
                  bold: true,
                  fontSize: 16,
                  noWrap: true,
                  margin: [0, 0, 0, 5],
                },
                {
                  text: "PATIENT CASE STUDIES",
                  bold: true,
                  fontSize: 11,
                },
              ],
            },
            {
              width: 60,
              text: "",
            },
          ],
        },
        ...(currentPage > 1
          ? [
              {
                margin: [0, 5, 0, 0],
                columns: [
                  {
                    width: "*",
                    text: [{ text: "Name: ", bold: true }, patient.name],
                  },
                  {
                    width: "auto",
                    alignment: "right",
                    text: [
                      { text: "MAIN: ", bold: true },
                      String(patient.main),
                    ],
                  },
                ],
                fontSize: 8,
              },
            ]
          : []),
      ],
    }),
    content: [
      {
        // Preserve page 1 while continuation pages use the tighter top margin.
        margin: [0, 25, 0, 10],
        columns: [
          {
            width: "*",
            stack: [
              {
                text: "Patient Information:",
                color: "#176fad",
                bold: true,
                fontSize: 8,
                margin: [0, 0, 0, 2],
              },
              {
                text: [{ text: "MAIN: ", bold: true }, String(patient.main)],
              },
              {
                text: [{ text: "Name: ", bold: true }, patient.name],
              },
              {
                text: [
                  { text: "Phone number: ", bold: true },
                  String(patient.phone),
                ],
              },
              {
                text: [{ text: "Address: ", bold: true }, patient.address],
              },
            ],
            fontSize: 7,
            lineHeight: 1.25,
          },
          {
            width: 120,
            stack: [
              {
                text: [{ text: "Age: ", bold: true }, String(patient.age)],
              },
              {
                text: [
                  { text: "Gender: ", bold: true },
                  String(patient.gender),
                ],
              },
            ],
            fontSize: 7,
            lineHeight: 1.25,
          },
        ],
      },
      ...(caseStudies || []).map(createCaseStudySection),
    ],
    footer: (currentPage, pageCount) => ({
      margin: [40, 0, 40, 8],
      columns: [
        {
          width: "*",
          stack: [
            {
              text: PATIENT_CASE_STUDY_HOSPITAL_NAME,
              color: "#176fad",
              bold: true,
              fontSize: 7,
            },
            { text: HOSPITAL_ADDRESS, fontSize: 6.5 },
            {
              text: `Mob: ${PHONE_NO} | Email: ${EMAIL_ID}`,
              fontSize: 6.5,
            },
          ],
        },
        {
          width: 50,
          text: `${currentPage} of ${pageCount}`,
          alignment: "right",
          fontSize: 7,
          margin: [0, 14, 0, 0],
        },
      ],
    }),
    defaultStyle: {
      fontSize: 8,
      color: "#111827",
    },
  }));
};

// Open the tab during the click event to avoid popup blocking.
export const openPatientCaseStudyPdfTab = () => {
  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    throw new Error(
      "The Patient Case Study PDF tab was blocked by the browser.",
    );
  }

  return printWindow;
};

export const renderPatientCaseStudyPdfTab = (printWindow, docDefinition) => {
  if (!printWindow || printWindow.closed) {
    throw new Error("The Patient Case Study PDF tab is unavailable.");
  }

  PdfPrinter.createPdf(docDefinition, null, null, fonts.pdfMake.vfs).open(
    {},
    printWindow,
  );
};
